import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, Mail, Check, Clock, Car } from "lucide-react";
import { THERAPIST, SERVICES } from "@/data/catalog";
import useSEO from "@/hooks/useSEO";

/**
 * Local SEO landing page — dedicated pages for Blainville and Rive-Nord.
 * Content is optimized for local search intent (e.g. "massothérapie Blainville").
 */
export default function LocalPage({ location }) {
    const isBlainville = location === "blainville";
    const cityName = isBlainville ? "Blainville" : "Rive-Nord";
    const slug = isBlainville ? "blainville" : "rive-nord";
    const canonical = `https://massoclic.ca/${slug}`;

    // Neighboring cities served (used in both pages, weighted per location)
    const serviceArea = isBlainville
        ? ["Blainville", "Boisbriand", "Sainte-Thérèse", "Rosemère", "Lorraine", "Bois-des-Filion", "Mirabel"]
        : ["Blainville", "Terrebonne", "Mascouche", "Repentigny", "Boisbriand", "Sainte-Thérèse", "Rosemère", "Lorraine", "Sainte-Anne-des-Plaines", "Mirabel", "Saint-Eustache", "Deux-Montagnes"];

    const title = isBlainville
        ? "Massothérapie à Blainville · Jérémy Tessier — Massage bien-être"
        : "Massothérapie sur la Rive-Nord · Jérémy Tessier — Blainville et environs";

    const description = isBlainville
        ? "Massothérapie professionnelle à Blainville : massage classique, signature, biocorporel et enrichi. Cabinet chaleureux sur le boulevard Curé-Labelle. Réservation en ligne."
        : "Massothérapie bien-être desservant la Rive-Nord (Blainville, Terrebonne, Mascouche, Boisbriand, Sainte-Thérèse, Rosemère, Mirabel). Cabinet à Blainville. Réservation en ligne.";

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "HealthAndBeautyBusiness",
        "@id": `${canonical}#business`,
        name: "Jérémy Tessier — Massothérapie",
        description,
        url: canonical,
        image: "https://massoclic.ca/icon.svg",
        telephone: THERAPIST.phone,
        email: THERAPIST.email,
        priceRange: "$$",
        address: {
            "@type": "PostalAddress",
            streetAddress: "1436 boulevard Curé-Labelle",
            addressLocality: "Blainville",
            addressRegion: "QC",
            postalCode: "J7C 2P4",
            addressCountry: "CA",
        },
        geo: {
            "@type": "GeoCoordinates",
            latitude: 45.6742,
            longitude: -73.8837,
        },
        areaServed: serviceArea.map((c) => ({
            "@type": "City",
            name: c,
            containedInPlace: { "@type": "AdministrativeArea", name: "Québec" },
        })),
        openingHoursSpecification: [
            {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                opens: "09:00",
                closes: "20:00",
            },
        ],
        makesOffer: SERVICES.map((s) => ({
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: s.name, description: s.description },
        })),
        sameAs: ["https://massoclic.ca"],
    };

    useSEO({
        title,
        description,
        canonical,
        ogImage: "https://massoclic.ca/icon.svg",
        jsonLd,
    });

    return (
        <main data-testid={`local-${slug}-page`} className="overflow-hidden">
            {/* Hero */}
            <section className="relative pt-32 md:pt-40 pb-16 md:pb-24">
                <div className="absolute inset-0 -z-10">
                    <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-[#3D8C95]/10 blur-3xl" />
                    <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#E07A5F]/10 blur-3xl" />
                </div>
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E8E2D9] mb-6">
                        <MapPin size={14} className="text-[#E07A5F]" />
                        <span className="text-xs tracking-[0.2em] uppercase text-[#6E6862]">
                            {isBlainville ? "Cabinet à Blainville" : "Service Rive-Nord"}
                        </span>
                    </div>
                    <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-light text-[#2C2A29] tracking-tight leading-[1.05]">
                        {isBlainville ? "Massothérapie" : "Massothérapie"}{" "}
                        <span className="italic text-[#E07A5F]">à {cityName}</span>
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#6E6862] leading-relaxed max-w-2xl">
                        {isBlainville
                            ? "Cabinet chaleureux au 1436 boulevard Curé-Labelle, à Blainville. Massage classique, signature, biocorporel et enrichi — chaque soin est adapté à votre corps, à votre rythme et à votre histoire."
                            : "Basé à Blainville, je reçois avec plaisir la clientèle de toute la Rive-Nord. Un espace calme, professionnel et accueillant pour votre bien-être — sans jugement, sans précipitation."}
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link to="/#reservation">
                            <Button
                                data-testid="local-cta-book"
                                className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-8 py-6 text-base shadow-[0_10px_30px_rgba(224,122,95,0.3)]"
                            >
                                Réserver en ligne
                            </Button>
                        </Link>
                        <a href={`tel:${THERAPIST.phone}`}>
                            <Button
                                variant="outline"
                                className="rounded-full border-[#3D8C95] text-[#3D8C95] hover:bg-[#3D8C95] hover:text-white px-8 py-6 text-base"
                            >
                                <Phone size={16} className="mr-2" />
                                {THERAPIST.phone}
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* Service area — highlights local intent */}
            <section className="py-16 md:py-24 bg-white">
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.3em] uppercase text-[#3D8C95] mb-4">
                        Municipalités desservies
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29] leading-tight">
                        {isBlainville
                            ? "Votre massothérapeute local à Blainville"
                            : "Au service de toute la Rive-Nord"}
                    </h2>
                    <p className="mt-4 text-[#6E6862] leading-relaxed max-w-3xl">
                        Que vous veniez de {serviceArea.slice(0, 3).join(", ")} ou d&apos;ailleurs sur la
                        couronne nord, le cabinet est facilement accessible depuis les autoroutes 15 et 640.
                        Stationnement gratuit sur place.
                    </p>
                    <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {serviceArea.map((c) => (
                            <div
                                key={c}
                                data-testid={`city-${c.toLowerCase()}`}
                                className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-[#FAF7F2] border border-[#E8E2D9]"
                            >
                                <Check size={16} className="text-[#3D8C95] flex-shrink-0" />
                                <span className="text-sm text-[#2C2A29]">{c}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Practical info */}
            <section className="py-16 md:py-24">
                <div className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-6">
                    <div className="rounded-3xl p-6 bg-white border border-[#E8E2D9]">
                        <MapPin size={24} className="text-[#E07A5F] mb-3" />
                        <h3 className="font-display text-xl text-[#2C2A29]">Adresse</h3>
                        <p className="mt-2 text-sm text-[#6E6862]">
                            1436 boulevard Curé-Labelle<br />Blainville, QC J7C 2P4
                        </p>
                    </div>
                    <div className="rounded-3xl p-6 bg-white border border-[#E8E2D9]">
                        <Clock size={24} className="text-[#3D8C95] mb-3" />
                        <h3 className="font-display text-xl text-[#2C2A29]">Horaires</h3>
                        <p className="mt-2 text-sm text-[#6E6862]">
                            Lundi au samedi<br />9 h — 20 h
                        </p>
                    </div>
                    <div className="rounded-3xl p-6 bg-white border border-[#E8E2D9]">
                        <Car size={24} className="text-[#C44569] mb-3" />
                        <h3 className="font-display text-xl text-[#2C2A29]">Accès</h3>
                        <p className="mt-2 text-sm text-[#6E6862]">
                            Autoroutes 15 & 640<br />Stationnement gratuit
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA footer */}
            <section className="py-16 md:py-24 text-center">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <h2 className="font-display text-3xl md:text-5xl text-[#2C2A29] leading-tight">
                        Prêt à prendre soin de vous ?
                    </h2>
                    <p className="mt-4 text-[#6E6862] leading-relaxed">
                        Choisissez votre soin et votre créneau — confirmation instantanée par courriel.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3 justify-center">
                        <Link to="/#reservation">
                            <Button
                                data-testid="local-cta-book-2"
                                className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-8 py-6 text-base shadow-[0_10px_30px_rgba(224,122,95,0.3)]"
                            >
                                Réserver maintenant
                            </Button>
                        </Link>
                        <a href={`mailto:${THERAPIST.email}`}>
                            <Button
                                variant="outline"
                                className="rounded-full border-[#3D8C95] text-[#3D8C95] hover:bg-[#3D8C95] hover:text-white px-8 py-6 text-base"
                            >
                                <Mail size={16} className="mr-2" />
                                Écrire un courriel
                            </Button>
                        </a>
                    </div>
                </div>
            </section>
        </main>
    );
}
